import type { FormBuilderData } from "@/features/forms/api/formBuilderApi";
import { ALLOWED_FORM_DRAFT_INPUT_KEYS, type FormDraft, type FormDraftInput, sanitiseFormDraft } from "./formDraft";

type BuilderPage = FormBuilderData["pages"][number];
type BuilderInput = BuilderPage["inputs"][number];

interface FormRevisionSource {
    form_name: string | null;
    form_description: string | null;
    pages: Array<{
        page_id: string;
        page_label: string | null;
        page_description: string | null;
        inputs: Array<{
            input_id: string;
            input_key: string;
            input_label: string | null;
            input_description: string | null;
            is_optional: boolean;
            options?: string[] | null;
            scale_options?: unknown;
            min_value?: number | null;
            max_value?: number | null;
        }>;
    }>;
}

export function formBuilderDataToDraft(form: FormRevisionSource): FormDraft | null {
    const result = sanitiseFormDraft({
        form_name: form.form_name,
        form_description: form.form_description,
        pages: form.pages.flatMap((page) => {
            const inputs = page.inputs.filter((input) =>
                ALLOWED_FORM_DRAFT_INPUT_KEYS.includes(
                    input.input_key as (typeof ALLOWED_FORM_DRAFT_INPUT_KEYS)[number]
                )
            );
            return inputs.length > 0 ? [{ ...page, inputs }] : [];
        }),
    });
    return result.draft;
}

export function validateFormRevisionIds(current: FormDraft, proposal: FormDraft): boolean {
    const currentPageIds = current.pages.flatMap((page) => (page.page_id ? [page.page_id] : []));
    const proposalPageIds = proposal.pages.flatMap((page) => (page.page_id ? [page.page_id] : []));
    const currentInputIds = current.pages.flatMap((page) =>
        page.inputs.flatMap((input) => (input.input_id ? [input.input_id] : []))
    );
    const proposalInputIds = proposal.pages.flatMap((page) =>
        page.inputs.flatMap((input) => (input.input_id ? [input.input_id] : []))
    );

    const matchesExactly = (currentIds: string[], proposalIds: string[]) =>
        proposalIds.length === currentIds.length &&
        new Set(proposalIds).size === proposalIds.length &&
        currentIds.every((id) => proposalIds.includes(id));

    return matchesExactly(currentPageIds, proposalPageIds) && matchesExactly(currentInputIds, proposalInputIds);
}

export function summariseFormRevision(current: FormDraft, proposal: FormDraft) {
    const currentPages = new Map(
        current.pages.flatMap((page) => (page.page_id ? [[page.page_id, page] as const] : []))
    );
    const currentInputs = new Map(
        current.pages.flatMap((page) =>
            page.inputs.flatMap((input) => (input.input_id ? [[input.input_id, input] as const] : []))
        )
    );
    const proposalPageIds = new Set(proposal.pages.flatMap((page) => (page.page_id ? [page.page_id] : [])));
    const proposalInputIds = new Set(
        proposal.pages.flatMap((page) => page.inputs.flatMap((input) => (input.input_id ? [input.input_id] : [])))
    );
    const withoutId = ({ input_id: _inputId, ...input }: FormDraft["pages"][number]["inputs"][number]) => input;

    return {
        changedForm: current.form_name !== proposal.form_name || current.form_description !== proposal.form_description,
        changedPages: proposal.pages.filter((page) => {
            const previous = page.page_id ? currentPages.get(page.page_id) : null;
            return (
                previous != null &&
                (previous.page_label !== page.page_label || previous.page_description !== page.page_description)
            );
        }).length,
        changedQuestions: proposal.pages
            .flatMap((page) => page.inputs)
            .filter((input) => {
                const previous = input.input_id ? currentInputs.get(input.input_id) : null;
                return previous != null && JSON.stringify(withoutId(previous)) !== JSON.stringify(withoutId(input));
            }).length,
        addedPages: proposal.pages.filter((page) => !page.page_id).length,
        addedQuestions: proposal.pages.flatMap((page) => page.inputs).filter((input) => !input.input_id).length,
        removed:
            [...currentPages.keys()].filter((id) => !proposalPageIds.has(id)).length +
            [...currentInputs.keys()].filter((id) => !proposalInputIds.has(id)).length,
    };
}

function draftInputToRow(pageId: string, input: FormDraftInput, inputIndex: number): BuilderInput {
    return {
        input_id: input.input_id ?? crypto.randomUUID(),
        page_id: pageId,
        input_key: input.input_key,
        input_label: input.input_label,
        input_description: input.input_description ?? null,
        input_index: inputIndex,
        is_optional: input.is_optional === true,
        is_editable: true,
        allow_multiple: input.input_key === "multiple-choice",
        orientation: "vertical",
        options: input.options ?? null,
        scale_options: input.scale_options ?? null,
        min_value: input.min_value ?? null,
        max_value: input.max_value ?? null,
        chart_order: null,
        count: null,
        created_at: new Date().toISOString(),
        decimal_points: null,
        input_caption: null,
        input_list: null,
        is_primary_score: false,
        max_count: null,
        max_length: null,
        max_score: null,
        min_count: null,
        rules_id: null,
        score_target: null,
        subscale: null,
        unit: null,
    } as BuilderInput;
}

function draftPageToRow(formId: string, page: FormDraft["pages"][number], pageIndex: number): BuilderPage {
    const pageId = page.page_id ?? crypto.randomUUID();
    return {
        page_id: pageId,
        form_id: formId,
        page_label: page.page_label,
        page_description: page.page_description ?? null,
        page_index: pageIndex,
        page_logic: null,
        inputs: page.inputs.map((input, inputIndex) => draftInputToRow(pageId, input, inputIndex + 1)),
        blocks: [],
    } as BuilderPage;
}

export function formDraftToBuilderData(draft: FormDraft): FormBuilderData {
    const formId = crypto.randomUUID();
    return {
        form_id: formId,
        form_name: draft.form_name,
        form_description: draft.form_description,
        form_instructions: null,
        instructions_before_start: false,
        form_caption: null,
        status: "draft",
        is_published: false,
        is_licensed: false,
        is_side_specific: false,
        show_chart: false,
        score_engine: null,
        score_engine_config: null,
        slug: null,
        scoring: null,
        prevalence: null,
        time_to_complete: null,
        medical_specialties: null,
        indications: null,
        orthopaedic_areas: null,
        canonical_form_id: null,
        chart_type: null,
        copyright_info: null,
        created_at: new Date().toISOString(),
        developer_info: null,
        image_id: null,
        importance_level: null,
        interpretation: null,
        limitations: null,
        locale: null,
        meaningful_change_threshold: null,
        overview_text: null,
        price: null,
        required_input_keys: null,
        scoring_methodology: null,
        subscales: null,
        hiddenInputs: [],
        pages: draft.pages.map((page, index) => draftPageToRow(formId, page, index + 1)),
    } as FormBuilderData;
}

export function applyFormDraftToBuilder(form: FormBuilderData, draft: FormDraft): FormBuilderData {
    const pages = form.pages.map((page) => {
        const proposed = draft.pages.find((item) => item.page_id === page.page_id);
        if (!proposed) return page;

        const inputs = page.inputs.map((input) => {
            const nextInput = proposed.inputs.find((item) => item.input_id === input.input_id);
            return nextInput
                ? {
                      ...input,
                      input_key: nextInput.input_key,
                      input_label: nextInput.input_label,
                      input_description: nextInput.input_description ?? null,
                      is_optional: nextInput.is_optional === true,
                      allow_multiple: nextInput.input_key === "multiple-choice",
                      options: nextInput.options ?? null,
                      scale_options: nextInput.scale_options ?? null,
                      min_value: nextInput.min_value ?? null,
                      max_value: nextInput.max_value ?? null,
                  }
                : input;
        });
        const extraInputs = proposed.inputs.filter((input) => !input.input_id);
        const nextIndex = inputs.reduce((max, input) => Math.max(max, input.input_index), 0);

        return {
            ...page,
            page_label: proposed.page_label,
            page_description: proposed.page_description ?? null,
            inputs: [
                ...inputs,
                ...extraInputs.map((input, index) => draftInputToRow(page.page_id, input, nextIndex + index + 1)),
            ],
        };
    });

    const extraPages = draft.pages.filter((page) => !page.page_id);
    const nextPageIndex = pages.reduce((max, page) => Math.max(max, page.page_index), 0);

    return {
        ...form,
        form_name: draft.form_name,
        form_description: draft.form_description,
        pages: [
            ...pages,
            ...extraPages.map((page, index) => draftPageToRow(form.form_id, page, nextPageIndex + index + 1)),
        ],
    };
}
